import axiosInstance from '../api';
import { PAGINATION } from '../../config/constants';
import logger from '../../utils/logger';
import {
  Notification,
  NotificationsResponse,
  UnreadCountResponse,
  ReadStatusResponse,
  AdminNotificationRequest,
  AdminNotificationResponse,
  MockNotificationData,
  NOTIFICATIONS_STORAGE_KEY,
  LOCALLY_UNREAD_KEY
} from './notificationService.types';

const API_URL = '/notifications';

// 開発用フラグ：trueの場合、APIエラー時にモック応答を返す
const USE_MOCK_ON_ERROR = true;

/**
 * モックの通知データを取得する
 * @returns モックの通知データ
 */
const getMockNotifications = (): MockNotificationData => {
  try {
    const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as MockNotificationData;
    }
  } catch (e) {
    console.error('モック通知の取得エラー:', e);
  }
  
  // デフォルトのモックデータ（サンプルデータ）
  const mockNotifications: Notification[] = [
    {
      id: 1,
      user_id: 1,
      message: 'あなたの動画「医療従事者のためのガイドライン」がいいねされました',
      reference_type: 'video',
      reference_id: 1,
      video_id: 1,
      is_read: false,
      created_at: new Date(Date.now() - 3600000).toISOString(), // 1時間前
      updated_at: new Date(Date.now() - 3600000).toISOString()
    },
    {
      id: 2,
      user_id: 1,
      message: '田中医師があなたの動画にコメントしました：「非常に参考になりました」',
      reference_type: 'comment',
      reference_id: 101,
      video_id: 2,
      is_read: false,
      created_at: new Date(Date.now() - 86400000).toISOString(), // 1日前
      updated_at: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: 3,
      user_id: 1,
      message: 'システムメンテナンスのお知らせ：本日22時〜23時までメンテナンスを実施します',
      reference_type: 'system',
      reference_id: null,
      video_id: null,
      is_read: true,
      created_at: new Date(Date.now() - 259200000).toISOString(), // 3日前
      updated_at: new Date(Date.now() - 259200000).toISOString()
    }
  ];
  
  // モックデータを保存してから返す
  const unreadCount = mockNotifications.filter(n => !n.is_read).length;
  const mockData: MockNotificationData = { notifications: mockNotifications, unreadCount };
  saveMockNotifications(mockNotifications);
  
  return mockData;
};

/**
 * モック通知を保存する
 * @param notifications 通知リスト
 */
const saveMockNotifications = (notifications: Notification[]): void => {
  try {
    const unreadCount = notifications.filter(n => !n.is_read).length;
    logger.devLog('保存するモックデータ:', { notifications, unreadCount });
    
    // フォーマットを統一して保存
    localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, JSON.stringify({
      notifications,
      unreadCount
    }));
  } catch (e) {
    console.error('モック通知の保存エラー:', e);
  }
};

/**
 * ローカルで未読に戻した通知IDのリストを取得
 * @returns 未読に戻した通知IDの配列
 */
const getLocallyUnreadIds = (): (number | string)[] => {
  try {
    const stored = localStorage.getItem(LOCALLY_UNREAD_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('ローカル未読情報の取得エラー:', e);
  }
  return [];
};

/**
 * ローカルで未読に戻した通知IDを保存
 * @param notificationId 通知ID
 */
const saveLocallyUnreadId = (notificationId: number | string): void => {
  try {
    const unreadIds = getLocallyUnreadIds();
    if (!unreadIds.includes(notificationId)) {
      unreadIds.push(notificationId);
      localStorage.setItem(LOCALLY_UNREAD_KEY, JSON.stringify(unreadIds));
      logger.devLog(`通知ID ${notificationId} をローカル未読リストに追加しました`);
    }
  } catch (e) {
    console.error('ローカル未読情報の保存エラー:', e);
  }
};

/**
 * ローカルで未読に戻した通知IDを削除
 * @param notificationId 通知ID
 */
const removeLocallyUnreadId = (notificationId: number | string): void => {
  try {
    const unreadIds = getLocallyUnreadIds();
    const newUnreadIds = unreadIds.filter(id => 
      id !== notificationId && 
      String(id) !== String(notificationId)
    );
    localStorage.setItem(LOCALLY_UNREAD_KEY, JSON.stringify(newUnreadIds));
    logger.devLog(`通知ID ${notificationId} をローカル未読リストから削除しました`);
  } catch (e) {
    console.error('ローカル未読情報の削除エラー:', e);
  }
};

/**
 * 通知データにローカルの未読状態を適用
 * @param notifications 通知データの配列
 * @returns ローカルの未読状態を適用した通知データ
 */
const applyLocallyUnreadStatus = (notifications: Notification[]): Notification[] => {
  const locallyUnreadIds = getLocallyUnreadIds();
  if (locallyUnreadIds.length === 0) return notifications;
  
  logger.devLog('ローカルの未読状態を適用します。対象ID:', locallyUnreadIds);
  return notifications.map(notification => {
    const notificationId = notification.id;
    if (locallyUnreadIds.some(id => 
      id === notificationId || 
      String(id) === String(notificationId)
    )) {
      logger.devLog(`通知ID ${notificationId} にローカルの未読状態を適用しました`);
      return { 
        ...notification, 
        is_read: false, 
        isRead: false 
      };
    }
    return notification;
  });
};

/**
 * APIレスポンスの通知データを正規化する
 * @param notifications 通知データの配列
 * @returns 正規化された通知データ
 */
const normalizeNotifications = (notifications: any[]): Notification[] => {
  return notifications.map(notification => {
    const isReadValue = 
      notification.isRead === false || notification.isRead === 0 || notification.isRead === "false" || 
      notification.is_read === false || notification.is_read === 0 || notification.is_read === "false"
        ? false
        : true;
    
    let videoId: number | null = null;
    
    if (notification.video_id) {
      videoId = notification.video_id;
    } 
    else if (notification.video && notification.video.id) {
      videoId = notification.video.id;
    }
    else if (notification.reference_type === 'video' && notification.reference_id) {
      videoId = notification.reference_id;
    }

    logger.devLog(`通知ID: ${notification.id} のvideo_id: ${videoId}`);
    
    return {
      ...notification,
      is_read: isReadValue,
      isRead: isReadValue,
      video_id: videoId
    } as Notification;
  });
};

/**
 * ユーザーの通知一覧を取得
 * @param page ページ番号
 * @param limit 1ページあたりの件数
 * @returns 通知一覧データ
 */
export const getNotifications = async (page = 1, limit = 10): Promise<NotificationsResponse> => {
  try {
    logger.devLog(`通知一覧取得 (ページ:${page}, 件数:${limit})`);
    const response = await axiosInstance.get<any>(`${API_URL}?page=${page}&limit=${limit}`);
    logger.devLog('API response:', response.data);
    
    if (!response.data) {
      throw new Error('APIレスポンスが空です');
    }
    
    let notifications: any[] = [];
    let additionalData: Record<string, number> = {};
    
    if (response.data.success) {
      if (response.data.data) {
        const dataObj = response.data.data;
        
        if (Array.isArray(dataObj.notifications)) {
          notifications = dataObj.notifications;
          additionalData = {
            totalCount: dataObj.totalCount || dataObj.total || notifications.length,
            unreadCount: dataObj.unreadCount || 0,
            currentPage: dataObj.currentPage || page,
            totalPages: dataObj.totalPages || 1
          };
        } 
        else if (Array.isArray(dataObj)) {
          notifications = dataObj;
        }
      }
    } 
    else if (Array.isArray(response.data)) {
      notifications = response.data;
    }
    
    if (!notifications || notifications.length === 0) {
      console.warn('通知データが見つかりませんでした');
      return {
        success: true,
        data: {
          notifications: [],
          ...additionalData
        }
      };
    }
    
    const normalizedNotifications = normalizeNotifications(notifications);
    
    const finalNotifications = applyLocallyUnreadStatus(normalizedNotifications);
    
    logger.devLog(`取得した通知: ${finalNotifications.length}件`);
    
    return {
      success: true,
      data: {
        notifications: finalNotifications,
        ...additionalData
      }
    };
  } catch (error) {
    console.error('通知取得エラー:', error);
    
    if (USE_MOCK_ON_ERROR) {
      console.warn('モック通知データを使用します');
      const mockData = getMockNotifications();
      logger.devLog('Mock data:', mockData);
      
      let mockNotifications: Notification[] = [];
      if (mockData.notifications) {
        mockNotifications = normalizeNotifications(mockData.notifications);
      }
      
      return {
        success: true,
        data: {
          notifications: mockNotifications,
          total: mockNotifications.length,
          page: 1,
          limit: PAGINATION.NOTIFICATION_PAGE_SIZE,
          unreadCount: mockNotifications.filter(n => !n.is_read).length
        }
      };
    }
    
    throw error;
  }
};

/**
 * 未読通知数を取得
 * @returns 未読通知数
 */
export const getUnreadCount = async (): Promise<number> => {
  try {
    const response = await axiosInstance.get<UnreadCountResponse>(`${API_URL}/unread-count`);
    logger.devLog('未読数API応答:', response.data);
    if (response.data && response.data.data && typeof response.data.data.count === 'number') {
      return response.data.data.count;
    }
    return 0;
  } catch (error) {
    console.error('未読通知数取得エラー:', error);
    
    if (USE_MOCK_ON_ERROR) {
      console.warn('モック未読カウントを使用します');
      const mockData = getMockNotifications();
      return mockData.unreadCount || 0;
    }
    
    return 0;
  }
};

/**
 * 通知を既読にする
 * @param notificationId 通知ID
 * @returns 処理結果
 */
export const markAsRead = async (notificationId: number): Promise<ReadStatusResponse> => {
  try {
    logger.devLog(`通知ID: ${notificationId} を既読にします...`);
    
    removeLocallyUnreadId(notificationId);
    
    const response = await axiosInstance.put<ReadStatusResponse>(`${API_URL}/${notificationId}/read`);
    logger.devLog('既読API応答:', response.data);
    
    if (response.data && response.data.success) {
      if (USE_MOCK_ON_ERROR) {
        updateMockNotificationReadStatus(notificationId, true);
      }
      
      return {
        success: true,
        message: '通知を既読にしました。',
        data: response.data.data
      };
    } else {
      throw new Error(response.data?.message || '既読化APIが失敗しました');
    }
  } catch (error) {
    console.error('通知既読エラー:', error);
    
    if (error instanceof Error && error.hasOwnProperty('response')) {
      const errorWithResponse = error as { response?: { data?: any } };
      console.error('APIエラーレスポンス:', errorWithResponse.response?.data);
    }
    
    if (USE_MOCK_ON_ERROR) {
      console.warn('APIエラー: モック既読処理を使用します');
      updateMockNotificationReadStatus(notificationId, true);
      
      return {
        success: true,
        message: '通知を既読にしました（モック）'
      };
    }
    
    throw error;
  }
};

/**
 * 通知を未読に戻す
 * @param notificationId 通知ID
 * @returns 処理結果
 */
export const markAsUnread = async (notificationId: number): Promise<ReadStatusResponse> => {
  try {
    logger.devLog(`通知ID: ${notificationId} を未読に戻します...`);
    
    saveLocallyUnreadId(notificationId);
    
    const response = await axiosInstance.put<ReadStatusResponse>(`${API_URL}/${notificationId}/unread`);
    logger.devLog('未読化API応答:', response.data);
    
    if (response.data && response.data.success) {
      if (USE_MOCK_ON_ERROR) {
        updateMockNotificationReadStatus(notificationId, false);
      }
      
      return {
        success: true,
        message: '通知を未読に戻しました。',
        data: response.data.data
      };
    } else {
      throw new Error(response.data?.message || '未読化APIが失敗しました');
    }
  } catch (error) {
    console.error('通知未読エラー:', error);
    
    if (error instanceof Error && error.hasOwnProperty('response')) {
      const errorWithResponse = error as { response?: { data?: any } };
      console.error('APIエラーレスポンス:', errorWithResponse.response?.data);
    }
    
    if (USE_MOCK_ON_ERROR) {
      console.warn('APIエラー: モック未読処理を使用します');
      updateMockNotificationReadStatus(notificationId, false);
      
      return {
        success: true,
        message: '通知を未読に戻しました（モック）'
      };
    }
    
    throw error;
  }
};

/**
 * モック通知の既読/未読状態を更新する
 * @param notificationId 通知ID
 * @param isRead 既読状態
 */
const updateMockNotificationReadStatus = (notificationId: number, isRead: boolean): void => {
  try {
    const mockData = getMockNotifications();
    if (!mockData || !mockData.notifications) return;
    
    const notifications = mockData.notifications.map(notification => 
      notification.id === notificationId || 
      String(notification.id) === String(notificationId)
        ? { ...notification, is_read: isRead, isRead: isRead } 
        : notification
    );
    
    saveMockNotifications(notifications);
    logger.devLog(`モック通知ID ${notificationId} の状態を更新: ${isRead ? '既読' : '未読'}`);
  } catch (error) {
    console.error('モック通知の状態更新エラー:', error);
  }
};

/**
 * すべての通知を既読にする
 * @returns 処理結果
 */
export const markAllAsRead = async (): Promise<ReadStatusResponse> => {
  try {
    localStorage.setItem(LOCALLY_UNREAD_KEY, JSON.stringify([]));
    logger.devLog('ローカル未読リストをクリアしました');
    
    const response = await axiosInstance.put<ReadStatusResponse>(`${API_URL}/read-all`);
    logger.devLog('全既読API応答:', response.data);
    return response.data;
  } catch (error) {
    console.error('すべての通知既読エラー:', error);
    
    if (USE_MOCK_ON_ERROR) {
      console.warn('モック全既読処理を使用します');
      const mockData = getMockNotifications();
      const notifications = mockData.notifications.map(notification => 
        ({ ...notification, is_read: true })
      );
      
      saveMockNotifications(notifications);
      
      return {
        success: true,
        message: 'すべての通知を既読にしました（モック）'
      };
    }
    
    throw error;
  }
};

/**
 * 管理者が全ユーザーに対して通知を送信する
 * @param message 送信するメッセージ
 * @returns 送信結果
 */
export const sendAdminNotification = async (message: string): Promise<AdminNotificationResponse> => {
  try {
    logger.devLog('管理者通知送信中:', message);
    const payload: AdminNotificationRequest = { message };
    const response = await axiosInstance.post<AdminNotificationResponse>(`${API_URL}/admin/broadcast`, payload);
    logger.devLog('API response:', response.data);
    
    if (response.data.success) {
      return {
        success: true,
        message: response.data.message,
        data: response.data.data
      };
    } else {
      throw new Error(response.data.message || '通知の送信に失敗しました');
    }
  } catch (error) {
    console.error('管理者通知送信エラー:', error);
    
    if (USE_MOCK_ON_ERROR) {
      console.warn('モックデータで代用します');
      
      if (error instanceof Error) {
        const errorWithResponse = error as { response?: { data?: { message?: string } } };
        return {
          success: false,
          message: errorWithResponse.response?.data?.message || error.message || '通知の送信に失敗しました'
        };
      }
      
      return {
        success: false,
        message: '通知の送信に失敗しました'
      };
    }
    
    throw error;
  }
};

// デフォルトエクスポート
const notificationService = {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAsUnread,
  markAllAsRead,
  sendAdminNotification
};

export default notificationService; 
